import * as api from '@gitbook/api-client';

import { PricingFeatureCategory } from './table_feature_categories';
import {
    checkIsCommunityProductKey,
    checkIsNewProductKey,
    checkIsOldProductKey,
    NEW_PRICING_PRODUCTS,
    type NewProduct,
    OLD_PRICING_PRODUCTS,
    type OldProduct,
    type PricingProductDefinition,
} from './table_products';

/**
 * Union of all pricing features.
 */
type OldPricingFeatureKey = (typeof OLD_PRICING_FEATURES_INPUT)[number]['key'];
type NewPricingFeatureKey = (typeof NEW_PRICING_FEATURES_INPUT)[number]['key'];
export type PricingFeatureKey = OldPricingFeatureKey | NewPricingFeatureKey;

/**
 * Product features map generic type
 */
export type FeatureMappedValues<
    T extends ReadonlyArray<{ key: string }>,
    ProductKeys extends keyof T[number],
> = {
    [K in T[number]['key']]: Extract<T[number], { key: K }>[ProductKeys];
};

/**
 * Pricing features maps
 */
type OldPricingFeatureMappedValues = FeatureMappedValues<
    typeof OLD_PRICING_FEATURES_INPUT,
    api.BillingProduct.Free | api.BillingProduct.Plus | api.BillingProduct.Pro
>;
type NewPricingFeatureMappedValues = FeatureMappedValues<
    typeof NEW_PRICING_FEATURES_INPUT,
    | api.BillingProduct.Free2024
    | api.BillingProduct.Plus2024
    | api.BillingProduct.Pro2024
    | api.BillingProduct.Enterprise2024
    | api.BillingProduct.Community2024
    | api.BillingProduct.Internal
>;

/**
 * Value associated in the pricing with a feature.
 */
export type OldPricingFeatureValue<FeatureKey extends OldPricingFeatureKey = OldPricingFeatureKey> =
    OldPricingFeatureMappedValues[FeatureKey];

export type NewPricingFeatureValue<FeatureKey extends NewPricingFeatureKey = NewPricingFeatureKey> =
    NewPricingFeatureMappedValues[FeatureKey];

/**
 * Mapping of pricing plans / "features sets" to their values.
 */
export type OldPricingFeatureAvailabilities<OldFeatureKey extends OldPricingFeatureKey> = {
    [key in OldProduct]: OldPricingFeatureValue<OldFeatureKey>;
};

export type NewPricingFeatureAvailabilities<NewFeatureKey extends NewPricingFeatureKey> = {
    [key in NewProduct]: NewPricingFeatureValue<NewFeatureKey>;
};

/**
 * Complete definition for a feature.
 */
type PricingFeatureDefinitionCommonProps = {
    // Generic name of the feature, e.g. `Unlimited members`
    title: string;

    // Tooltip label
    tooltipLabel: string;

    // Name of of the feature in the plans table
    plansTableTitle?: string;

    // Link to documentation, if available
    documentation?: string;

    // Category the feature belongs to, for grouped rendering in the plans table
    category: PricingFeatureCategory;
};
type OldPricingFeatureDefinition<
    OldFeatureKey extends OldPricingFeatureKey = OldPricingFeatureKey,
> = PricingFeatureDefinitionCommonProps & {
    key: OldFeatureKey;
} & OldPricingFeatureAvailabilities<OldFeatureKey>;

type NewPricingFeatureDefinition<
    NewFeatureKey extends NewPricingFeatureKey = NewPricingFeatureKey,
> = PricingFeatureDefinitionCommonProps & {
    key: NewFeatureKey;
} & NewPricingFeatureAvailabilities<NewFeatureKey>;

export type PricingFeatureDefinition = OldPricingFeatureDefinition | NewPricingFeatureDefinition;

export type OldPricingFeaturesTable = OldPricingFeatureDefinition[];
export type NewPricingFeaturesTable = NewPricingFeatureDefinition[];
export type PricingFeaturesTable = OldPricingFeaturesTable | NewPricingFeaturesTable;

/**
 * Get the maximum number of allowed members for an organization
 */
export function getMaxMembersLimit(plan: api.Organization['plan']): number {
    const maxMembersFeature = getFeatureDefinition(plan, 'max-members');

    if (!maxMembersFeature) {
        throw new Error(`max-members feature not found for plan: ${plan}`);
    }

    return maxMembersFeature[plan];
}

/**
 * Determiners whether the given organization has access to a feature
 */
export function hasAccessToFeature(
    plan: api.Organization['plan'],
    featureKey: Exclude<PricingFeatureKey, 'max-members'>
): boolean {
    const feature = getFeatureDefinition(plan, featureKey);

    if (!feature) {
        return false;
    }

    return feature[plan];
}

/**
 * Dynamic pricing feature tables.
 */
const OLD_PRICING_FEATURES_INPUT = [
    //
    // Usage
    //
    {
        key: 'max-members',
        title: 'Members',
        plansTableTitle: 'Seat limit',
        tooltipLabel: 'The maximum number of members in your organization.',
        category: PricingFeatureCategory.Usage,
        free: 1,
        plus: Number.POSITIVE_INFINITY,
        pro: Number.POSITIVE_INFINITY,
    },

    {
        key: 'spaces-and-collections',
        title: 'Spaces & collections',
        tooltipLabel:
            'Organize your content into as many spaces as you like, then group spaces into collections — all with customizable permissions and settings.',
        category: PricingFeatureCategory.Usage,
        free: Number.POSITIVE_INFINITY,
        plus: Number.POSITIVE_INFINITY,
        pro: Number.POSITIVE_INFINITY,
    },

    {
        key: 'docs-sites',
        title: 'Docs sites',
        tooltipLabel:
            'Customize and publish your content to a specific group or to the world using our built-in publishing tools.',
        category: PricingFeatureCategory.Usage,
        free: Number.POSITIVE_INFINITY,
        plus: Number.POSITIVE_INFINITY,
        pro: Number.POSITIVE_INFINITY,
    },

    //
    // Features
    //
    {
        key: 'git-sync',
        title: 'Git Sync',
        tooltipLabel:
            'Synchronize your GitBook content with GitHub or GitLab with our two-way sync integration.',
        category: PricingFeatureCategory.Features,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'change-requests',
        title: 'Change requests',
        tooltipLabel:
            'GitBook’s familiar branching workflow lets you make changes in a branch, request an optional review, and merge when ready.',
        category: PricingFeatureCategory.Features,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'version-history',
        title: 'Version history',
        tooltipLabel:
            'View and track historical changes, or share and rollback to any previous version.',
        category: PricingFeatureCategory.Features,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'commenting',
        title: 'Commenting',
        tooltipLabel:
            'Leave feedback on specific content blocks, @ mention other users and thread conversations for more detail.',
        category: PricingFeatureCategory.Features,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'integrations',
        title: 'Integrations',
        tooltipLabel:
            'Install one of our verified integrations (such as GitHub Sync, Linear and Slack) or build your own with our API.',
        category: PricingFeatureCategory.Features,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'importing',
        title: 'Importing',
        tooltipLabel:
            'Hit the ground running with our import tools for Markdown, Google Docs, Confluence, Notion, MS Word and more.',
        category: PricingFeatureCategory.Features,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'full-text-search',
        title: 'Full text search',
        tooltipLabel: 'Search your internal documentation for key words or phrases.',
        category: PricingFeatureCategory.Features,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'reviews',
        title: 'Review & approve edits',
        tooltipLabel:
            'Get feedback on change requests, ask for a review, and approve edits before they’re merged.',
        category: PricingFeatureCategory.Features,
        free: false,
        plus: true,
        pro: true,
    },

    {
        key: 'collaboration',
        title: 'Real-time collaboration',
        tooltipLabel:
            'Jump into any space with your team to make live edits and see their changes in real-time.',
        category: PricingFeatureCategory.Features,
        free: false,
        plus: true,
        pro: true,
    },

    {
        key: 'ai-lens',
        title: 'AI-powered instant answers',
        tooltipLabel:
            'Ask GitBook AI a question about your internal docs and get a summarized answer in seconds.',
        category: PricingFeatureCategory.Features,
        free: false,
        plus: false,
        pro: true,
    },

    {
        key: 'pdf-export',
        title: 'PDF Export',
        tooltipLabel: 'Easily export pages of your internal docs in PDF format.',
        category: PricingFeatureCategory.Features,
        free: false,
        plus: false,
        pro: true,
    },

    {
        key: 'ai-writing',
        title: 'AI writing and editing',
        tooltipLabel:
            'Use GitBook AI to write, edit, summarize, or translate content for you. It can even proof-read your documentation.',
        category: PricingFeatureCategory.Features,
        free: false,
        plus: false,
        pro: true,
    },

    {
        key: 'broken-links',
        title: 'Broken link detection',
        tooltipLabel:
            'Easily find and fix broken links to other GitBook pages within the space you’re editing.',
        category: PricingFeatureCategory.Unlisted,
        free: false,
        plus: false,
        pro: true,
    },

    {
        key: 'reusable-content',
        title: 'Reusable content',
        tooltipLabel:
            'Create reusable blocks of content that automatically sync across your entire documentation when they’re updated.',
        category: PricingFeatureCategory.Features,
        free: false,
        plus: false,
        pro: true,
    },

    //
    // Addons
    //
    {
        key: 'translations',
        title: 'AI Translations',
        tooltipLabel: 'Translate your content into multiple languages with AI.',
        category: PricingFeatureCategory.Features,
        free: false,
        plus: false,
        pro: false,
    },

    //
    // Docs sites
    //
    {
        key: 'page-view-insights',
        title: 'Page view insights',
        tooltipLabel:
            'See which pages your users are viewing the most and how your site traffic compares to the previous week.',
        category: PricingFeatureCategory.Sites,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'preview-deployments',
        title: 'Preview deployments',
        tooltipLabel:
            'See your content edits in context with unlimited full site previews before you deploy.',
        category: PricingFeatureCategory.Sites,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'api-playground',
        title: 'API playground',
        tooltipLabel: 'Let users test API endpoints directly in your docs using interactive blocks',
        category: PricingFeatureCategory.Sites,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'user-contribution',
        title: 'User contribution',
        tooltipLabel:
            'Allow your users to propose updates and improvements to your docs — enabled via Git Sync.',
        category: PricingFeatureCategory.Sites,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'seo',
        title: 'Built-in SEO',
        tooltipLabel:
            'GitBook handles the meta information, sitemap, responsive design and more for your site, so you don’t have to worry.',
        category: PricingFeatureCategory.Sites,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'site-variants',
        title: 'Site variants',
        tooltipLabel:
            'Publish multiple spaces on the same site to support localization and version releases for the same product.',
        category: PricingFeatureCategory.Sites,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'user-feedback',
        title: 'User feedback',
        tooltipLabel:
            'Understand how useful your content is by allowing your users to rate each page.',
        category: PricingFeatureCategory.Sites,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'custom-domain',
        title: 'Custom domain',
        tooltipLabel:
            'Publish your content with the URL of your choice (e.g. docs.yourcompany.com).',
        category: PricingFeatureCategory.Sites,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'basic-customization',
        title: 'Basic-customization',
        tooltipLabel:
            'Customize your site’s primary color and corner style, and enable a light/dark mode toggle.',
        category: PricingFeatureCategory.Sites,
        free: true,
        plus: true,
        pro: true,
    },

    {
        key: 'advanced-customization',
        title: 'Advanced customization',
        tooltipLabel:
            'Change your site’s logo, font, footer, and primary/header/header link colors to create highly branded docs.',
        category: PricingFeatureCategory.Sites,
        free: false,
        plus: false,
        pro: true,
    },

    {
        key: 'search-insights',
        title: 'Search insights',
        tooltipLabel:
            'See what your users are searching for on your site to help identify content gaps.',
        category: PricingFeatureCategory.Sites,
        free: false,
        plus: false,
        pro: true,
    },

    {
        key: 'unlisted-visibility',
        title: 'Opt out of search indexing',
        tooltipLabel:
            'Create a docs site that is not indexed by search engines. Ideal for publishing docs for things like beta programs or user testing.',
        category: PricingFeatureCategory.Sites,
        free: false,
        plus: false,
        pro: true,
    },

    {
        key: 'share-links',
        title: 'Private share links',
        tooltipLabel:
            'Publish a docs site that is only accessible to people with a unique link, which can be revoked at any time.',
        category: PricingFeatureCategory.Sites,
        free: false,
        plus: false,
        pro: true,
    },

    {
        key: 'visitor-authentication',
        title: 'Authenticated access',
        tooltipLabel:
            'Use an authentication integration or server-side code to control which specific users have access your documentation.',
        category: PricingFeatureCategory.Sites,
        free: false,
        plus: false,
        pro: true,
    },

    //
    // Member Management
    //
    {
        key: 'members-basic-roles',
        title: 'Basic roles',
        tooltipLabel:
            'Organize your team by assigning members the admin, creator, editor and reader roles.',
        category: PricingFeatureCategory.Members,
        free: false,
        plus: true,
        pro: true,
    },

    {
        key: 'members-advanced-roles',
        title: 'Advanced roles',
        tooltipLabel:
            'In addition to the basic roles, you can assign members the reviewer and commenter roles.',
        category: PricingFeatureCategory.Members,
        free: false,
        plus: false,
        pro: true,
    },

    {
        key: 'members-teams',
        title: 'Teams',
        tooltipLabel:
            'Group users into teams to make it easier to manage content access and permissions.',
        category: PricingFeatureCategory.Members,
        free: false,
        plus: false,
        pro: true,
    },
    {
        key: 'members-team-owners',
        title: 'Team owners',
        tooltipLabel: 'Delegate the management of a team to a specific member.',
        category: PricingFeatureCategory.Members,
        free: false,
        plus: false,
        pro: false,
    },

    //
    // Security
    //
    {
        key: 'security-email-sso',
        title: 'Google/email domain SSO',
        tooltipLabel:
            'Set up allowed email domains so anyone with an email address matching the domain can access your organization in GitBook.',
        category: PricingFeatureCategory.Security,
        free: true,
        plus: true,
        pro: true,
    },
    {
        key: 'security-saml',
        title: 'SAML SSO',
        tooltipLabel:
            'Allow organization members to sign into GitBook using a third-party identity provider.',
        category: PricingFeatureCategory.Security,
        free: false,
        plus: false,
        pro: true,
    },
    {
        key: 'security-legal',
        title: 'Legal & security reviews',
        tooltipLabel: `We'll work with your infoSec team to ensure a smooth procurement process.`,
        category: PricingFeatureCategory.Security,
        free: false,
        plus: false,
        pro: false,
    },

    //
    // Support
    //
    {
        key: 'support-email',
        title: 'Email support',
        tooltipLabel:
            'Log tickets via email or in-app with our support team. We address these tickets on a first-come, first-served basis.',
        category: PricingFeatureCategory.Support,
        free: true,
        plus: true,
        pro: true,
    },
    {
        key: 'support-priority',
        title: 'Priority support',
        tooltipLabel: 'Support tickets are placed in a priority queue for faster response times.',
        category: PricingFeatureCategory.Support,
        free: false,
        plus: false,
        pro: false,
    },
    {
        key: 'support-account-manager',
        title: 'Account manager',
        tooltipLabel:
            'A dedicated account manager to help you get the most out of GitBook on an ongoing basis.',
        category: PricingFeatureCategory.Support,
        free: false,
        plus: false,
        pro: false,
    },
    {
        key: 'support-invoicing',
        title: 'Custom contract & invoicing',
        tooltipLabel: 'Satisfy your finance team with payment by invoice.',
        category: PricingFeatureCategory.Support,
        free: false,
        plus: false,
        pro: false,
    },
    {
        key: 'support-onboarding',
        title: 'Migration support',
        tooltipLabel:
            'Get guidance and technical support when migrating your existing docs from legacy platforms.',
        category: PricingFeatureCategory.Support,
        free: false,
        plus: false,
        pro: false,
    },
] as const;

const NEW_PRICING_FEATURES_INPUT = [
    //
    // Usage
    //
    {
        key: 'max-members',
        title: 'Members',
        plansTableTitle: 'Seat limit',
        tooltipLabel: 'The maximum number of members in your organization.',
        category: PricingFeatureCategory.Usage,

        free_2024: 1,
        plus_2024: Number.POSITIVE_INFINITY,
        pro_2024: Number.POSITIVE_INFINITY,
        enterprise_2024: Number.POSITIVE_INFINITY,
        community_2024: Number.POSITIVE_INFINITY,
        internal: Number.POSITIVE_INFINITY,
    },

    {
        key: 'spaces-and-collections',
        title: 'Spaces & collections',
        tooltipLabel:
            'Organize your content into as many spaces as you like, then group spaces into collections — all with customizable permissions and settings.',
        category: PricingFeatureCategory.Usage,

        free_2024: Number.POSITIVE_INFINITY,
        plus_2024: Number.POSITIVE_INFINITY,
        pro_2024: Number.POSITIVE_INFINITY,
        enterprise_2024: Number.POSITIVE_INFINITY,
        community_2024: Number.POSITIVE_INFINITY,
        internal: Number.POSITIVE_INFINITY,
    },

    //
    // Features
    //
    {
        key: 'git-sync',
        title: 'Git Sync',
        tooltipLabel:
            'Synchronize your GitBook content with GitHub or GitLab with our two-way sync integration.',
        category: PricingFeatureCategory.Features,

        free_2024: true,
        plus_2024: true,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'change-requests',
        title: 'Change requests',
        tooltipLabel:
            'GitBook’s familiar branching workflow lets you make changes in a branch, request an optional review, and merge when ready.',
        category: PricingFeatureCategory.Features,

        free_2024: true,
        plus_2024: true,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'version-history',
        title: 'Version history',
        tooltipLabel:
            'View and track historical changes, or share and rollback to any previous version.',
        category: PricingFeatureCategory.Features,

        free_2024: true,
        plus_2024: true,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'commenting',
        title: 'Commenting',
        tooltipLabel:
            'Leave feedback on specific content blocks, @ mention other users and thread conversations for more detail.',
        category: PricingFeatureCategory.Features,

        free_2024: true,
        plus_2024: true,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'integrations',
        title: 'Integrations',
        tooltipLabel:
            'Install one of our verified integrations (such as GitHub Sync, Linear and Slack) or build your own with our API.',
        category: PricingFeatureCategory.Features,

        free_2024: true,
        plus_2024: true,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'importing',
        title: 'Importing',
        tooltipLabel:
            'Hit the ground running with our import tools for Markdown, Google Docs, Confluence, Notion, MS Word and more.',
        category: PricingFeatureCategory.Features,

        free_2024: true,
        plus_2024: true,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'full-text-search',
        title: 'Full text search',
        tooltipLabel: 'Search your internal documentation for key words or phrases.',
        category: PricingFeatureCategory.Features,

        free_2024: true,
        plus_2024: true,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'reviews',
        title: 'Review & approve edits',
        tooltipLabel:
            'Get feedback on change requests, ask for a review, and approve edits before they’re merged.',
        category: PricingFeatureCategory.Features,

        free_2024: false,
        plus_2024: true,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'collaboration',
        title: 'Real-time collaboration',
        tooltipLabel:
            'Jump into any space with your team to make live edits and see their changes in real-time.',
        category: PricingFeatureCategory.Features,

        free_2024: false,
        plus_2024: true,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'ai-lens',
        title: 'AI-powered instant answers',
        tooltipLabel:
            'Ask GitBook AI a question about your internal docs and get a summarized answer in seconds.',
        category: PricingFeatureCategory.Features,

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'pdf-export',
        title: 'PDF Export',
        tooltipLabel: 'Easily export pages of your internal docs in PDF format.',
        category: PricingFeatureCategory.Features,

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'ai-writing',
        title: 'AI writing and editing',
        tooltipLabel:
            'Use GitBook AI to write, edit, summarize, or translate content for you. It can even proof-read your documentation.',
        category: PricingFeatureCategory.Features,

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'broken-links',
        title: 'Broken link detection',
        tooltipLabel:
            'Easily find and fix broken links to other GitBook pages within the space you’re editing.',
        category: PricingFeatureCategory.Unlisted,

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'reusable-content',
        title: 'Reusable content',
        tooltipLabel:
            'Create reusable blocks of content that automatically sync across your entire documentation when they’re updated.',
        category: PricingFeatureCategory.Features,

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    //
    // Workflow
    //

    {
        key: 'change-requests-merge-rules',
        title: 'Merge rules',
        tooltipLabel: 'Configure merge rules to determine if change requests can be merged.',
        category: PricingFeatureCategory.Features,

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    //
    // Members Management
    //
    {
        key: 'basic-roles',
        title: 'Basic roles',
        tooltipLabel:
            'Organize your team by assigning members the admin, creator, editor and reader roles.',
        category: PricingFeatureCategory.Members,

        free_2024: true,
        plus_2024: true,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'members-advanced-roles',
        title: 'Advanced roles',
        tooltipLabel:
            'In addition to the basic roles, you can assign members the reviewer and commenter roles.',
        category: PricingFeatureCategory.Members,

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'members-teams',
        title: 'Teams',
        tooltipLabel:
            'Group users into teams to make it easier to manage content access and permissions.',
        category: PricingFeatureCategory.Members,

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'members-team-owners',
        title: 'Team owners',
        tooltipLabel: 'Delegate the management of a team to a specific member.',
        category: PricingFeatureCategory.Members,

        free_2024: false,
        plus_2024: false,
        pro_2024: false,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    //
    // Addons
    //
    {
        key: 'translations',
        title: 'AI Translations',
        tooltipLabel: 'Translate your content into multiple languages with AI.',
        category: PricingFeatureCategory.Features,

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    //
    // Security
    //
    {
        key: 'security-email-sso',
        title: 'Google/email domain SSO',
        tooltipLabel:
            'Set up allowed email domains so anyone with an email address matching the domain can access your organization in GitBook.',
        category: PricingFeatureCategory.Security,

        free_2024: true,
        plus_2024: true,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'security-saml',
        title: 'SAML SSO',
        tooltipLabel:
            'Allow organization members to sign into GitBook using a third-party identity provider.',
        category: PricingFeatureCategory.Security,

        free_2024: false,
        plus_2024: false,
        pro_2024: false,
        enterprise_2024: true,
        community_2024: false,
        internal: true,
    },

    {
        key: 'security-legal',
        title: 'Legal & security reviews',
        tooltipLabel: `We'll work with your infoSec team to ensure a smooth procurement process.`,
        category: PricingFeatureCategory.Security,

        free_2024: false,
        plus_2024: false,
        pro_2024: false,
        enterprise_2024: true,
        community_2024: false,
        internal: true,
    },

    //
    // Support
    //
    {
        key: 'support-email',
        title: 'Email support',
        tooltipLabel:
            'Log tickets via email or in-app with our support team. We address these tickets on a first-come, first-served basis.',
        category: PricingFeatureCategory.Support,

        free_2024: true,
        plus_2024: true,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    {
        key: 'support-priority',
        title: 'Priority support',
        tooltipLabel: 'Support tickets are placed in a priority queue for faster response times.',
        category: PricingFeatureCategory.Support,

        free_2024: false,
        plus_2024: false,
        pro_2024: false,
        enterprise_2024: true,
        community_2024: false,
        internal: true,
    },

    {
        key: 'support-account-manager',
        title: 'Account manager',
        tooltipLabel:
            'A dedicated account manager to help you get the most out of GitBook on an ongoing basis.',
        category: PricingFeatureCategory.Support,

        free_2024: false,
        plus_2024: false,
        pro_2024: false,
        enterprise_2024: true,
        community_2024: false,
        internal: true,
    },

    {
        key: 'support-invoicing',
        title: 'Custom contract & invoicing',
        tooltipLabel: 'Satisfy your finance team with payment by invoice.',
        category: PricingFeatureCategory.Support,

        free_2024: false,
        plus_2024: false,
        pro_2024: false,
        enterprise_2024: true,
        community_2024: false,
        internal: true,
    },

    {
        key: 'support-onboarding',
        title: 'Migration support',
        tooltipLabel:
            'Get guidance and technical support when migrating your existing docs from legacy platforms.',
        category: PricingFeatureCategory.Support,

        free_2024: false,
        plus_2024: false,
        pro_2024: false,
        enterprise_2024: true,
        community_2024: false,
        internal: true,
    },
] as const;

// PRICING_FEATURES_INPUTs are untyped arrays so that PricingFeatureKey can be extracted as an union.
// We want to validate that PRICING_FEATURES is a valid array of PricingFeatureDefinition.
export const OLD_PRICING_FEATURES: readonly OldPricingFeatureDefinition[] =
    OLD_PRICING_FEATURES_INPUT;
export const NEW_PRICING_FEATURES: readonly NewPricingFeatureDefinition[] =
    NEW_PRICING_FEATURES_INPUT;

export const OLD_PRICING_FEATURES_MAP = OLD_PRICING_FEATURES.reduce(
    (acc, feature) => {
        acc[feature.key] = feature;
        return acc;
    },
    {} as Partial<Record<OldPricingFeatureKey, OldPricingFeatureDefinition<OldPricingFeatureKey>>>
);
export const NEW_PRICING_FEATURES_MAP = NEW_PRICING_FEATURES.reduce(
    (acc, feature) => {
        acc[feature.key] = feature;
        return acc;
    },
    {} as Partial<Record<NewPricingFeatureKey, NewPricingFeatureDefinition<NewPricingFeatureKey>>>
);

/**
 * Helpers to retrieve features and features maps depending on the billing product.
 */
export function getFeaturesForBilling(product: api.BillingProduct) {
    return checkIsNewProductKey(product) ? NEW_PRICING_FEATURES : OLD_PRICING_FEATURES;
}

// Helper function to get a feature definition from the appropriate map
export function getFeatureDefinition(
    product: api.BillingProduct,
    featureKey: PricingFeatureKey | string | undefined
):
    | OldPricingFeatureDefinition<OldPricingFeatureKey>
    | NewPricingFeatureDefinition<NewPricingFeatureKey>
    | undefined {
    if (!featureKey) {
        return undefined;
    }

    if (checkIsNewProductKey(product)) {
        return NEW_PRICING_FEATURES_MAP[featureKey as NewPricingFeatureKey];
    } else {
        return OLD_PRICING_FEATURES_MAP[featureKey as OldPricingFeatureKey];
    }
}

/**
 * Get the features for a given product.
 */
function getProductFeatures(
    product: api.BillingProduct,
    features: typeof NEW_PRICING_FEATURES | typeof OLD_PRICING_FEATURES
) {
    return features.reduce<Record<string, boolean | number>>((acc, feature) => {
        acc[feature.key] = feature[product];
        return acc;
    }, {});
}

export type FeaturesComparison = {
    key: PricingFeatureKey;
    title: string;
    tooltipLabel: string;
    from: boolean | number;
    to: boolean | number;
    action: 'added' | 'removed';
}[];

/**
 * Compare features from one product to another.
 */
export function compareFeatures(
    from: api.BillingProduct,
    to: api.BillingProduct
): FeaturesComparison {
    if (from === to) {
        return [];
    }

    const features =
        checkIsOldProductKey(from) && checkIsOldProductKey(to)
            ? OLD_PRICING_FEATURES
            : NEW_PRICING_FEATURES;

    const fromFeatures = getProductFeatures(from, features);
    const toFeatures = getProductFeatures(to, features);

    return features.reduce<FeaturesComparison>((acc, feature) => {
        const key = feature.key;
        const fromValue = fromFeatures[key];
        const toValue = toFeatures[key];

        if (fromValue !== toValue) {
            const action = (() => {
                if (typeof fromValue === 'boolean' && typeof toValue === 'boolean') {
                    return toValue ? 'added' : 'removed';
                }
                return fromValue > toValue ? 'removed' : 'added';
            })();
            acc.push({
                key,
                title: feature.title,
                tooltipLabel: feature.tooltipLabel,
                from: fromValue,
                to: toValue,
                action,
            });
        }

        return acc;
    }, []);
}

/**
 * Test if the operation could be considered a downgrade and we need to make the user aware.
 * It returns a list of lost or reduced features.
 */
export function isPotentialDowngrade(
    productA: api.BillingProduct,
    productB: api.BillingProduct
): PricingFeatureDefinition[] {
    // Exit right away if the billing product is the same as the given product
    if (productA === productB) {
        return [];
    }

    const isOldProductA = checkIsOldProductKey(productA);
    const isOldProductB = checkIsOldProductKey(productB);
    const featureDefinitionsA = isOldProductA ? OLD_PRICING_FEATURES : NEW_PRICING_FEATURES;
    const featureDefinitionsB = isOldProductB ? OLD_PRICING_FEATURES : NEW_PRICING_FEATURES;

    return featureDefinitionsA.filter((featureDefinitionA) => {
        const featureKey = featureDefinitionA.key;
        const featureDefinitionB = featureDefinitionsB.find((obj) => obj.key === featureKey);

        if (!featureDefinitionB) {
            // If the feature doesn't exist on productB ignore it: it's likely deprecated
            return false;
        }

        // Get values to compare
        const valueA = featureDefinitionA[productA];
        const valueB = featureDefinitionB[productB];

        // Retrieve value types to verify whether we are dealing with comparable values
        const valueAType = typeof valueA;
        const valueBType = typeof valueB;

        // If types don't match, return false: we can't compare
        if (valueAType !== valueBType) {
            return false;
        }

        // If a feature was present in A but it's not in B, report it as lost
        if (valueAType !== 'undefined' && valueBType === 'undefined') {
            return true;
        }

        // Values have equal types and are defined: perform some type-specific checks
        switch (valueAType) {
            case 'number':
                // Report whether the value decreases
                return valueA > valueB;
            case 'boolean':
                // Report whether the value goes from `true` to `false`
                return valueA === true && valueB === false;
        }

        return false;
    });
}

/**
 * Find the first plan that includes a given feature
 */
export function findRequiredPlanForFeature(
    currentProduct: api.BillingProduct,
    featureKey: PricingFeatureKey
): PricingProductDefinition<api.BillingProduct> | undefined {
    const feature = getFeatureDefinition(currentProduct, featureKey);

    // If we didn't find the feature, it means it's not available on the current pricing.
    if (feature === undefined) {
        return;
    }

    const products = checkIsNewProductKey(currentProduct)
        ? NEW_PRICING_PRODUCTS
        : OLD_PRICING_PRODUCTS;

    return products.find(
        (product) => feature[product.key] && !checkIsCommunityProductKey(product.key)
    );
}

/**
 * Finds the first feature that requires an upgrade for the given organization plan.
 * Returns undefined if no upgrade is required.
 */
export function getBlockedFeature(
    featureKeys: PricingFeatureKey[],
    organizationPlan: api.BillingProduct
): PricingFeatureKey | undefined {
    return featureKeys.find((featureKey) => {
        if (featureKey === 'max-members') {
            // Show upgrade for old orgs on the free plan.
            return organizationPlan === api.BillingProduct.Free;
        }

        const feature = getFeatureDefinition(organizationPlan, featureKey);

        // Upgrade is required if feature doesn't exist in the current pricing
        if (!feature) {
            return true;
        }

        return !feature[organizationPlan];
    });
}
